import React, { useEffect, useState } from "react";
import DataTable from "../components/common/DataTable";
import { api_paths } from "../config/api.paths";
import API from "../config/axios";
import { useDispatch } from "react-redux";
import { SET_LOADING } from "../redux/user";
import { formatToUSDate } from "../utils/dateFormat";
import { PiArrowCircleDownThin, PiCheckCircleFill, PiXCircleFill } from "react-icons/pi"; // Added icons
import { toastErrorMessage } from "../utils/utils"; // Keep only for PDF errors
import LeftArrow from "../assets/images/committee/ArrowLeft.svg";
import { useLocation, useNavigate } from "react-router-dom";

// Simple Inline Modal Component for Payment Status
const PaymentStatusModal = ({ status, convergeId, onClose }) => {
  if (!status) return null;

  const isSuccess = status === "success";

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
      style={{ zIndex: 1050, backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div
        className="bg-white p-5 rounded-4 shadow text-center"
        style={{ maxWidth: "400px", width: "90%" }}
      >
        {/* ICON */}
        <div className="mb-3">
          {isSuccess ? (
            <PiCheckCircleFill className="text-success" size={60} />
          ) : (
            <PiXCircleFill className="text-danger" size={60} />
          )}
        </div>

        {/* TITLE */}
        <h3 className={`fw-bold mb-2 ${isSuccess ? "text-success" : "text-danger"}`}>
          {isSuccess ? "Payment Successful!" : "Payment Failed"}
        </h3>

        <p className="text-muted mb-4">
          {isSuccess
            ? "Thank you! Your transaction has been completed successfully."
            : "Something went wrong with your transaction. Please try again."}
        </p>

        {/* 🔥 SHOW CONVERGE ID WHEN AVAILABLE */}
        {convergeId && (
          <div className="mb-3">
            <strong>Transaction ID:</strong>
            <div className="text-break mt-1">{convergeId}</div>
          </div>
        )}

        <button
          onClick={onClose}
          className="btn btn-primary px-4 py-2 w-100 rounded-3 fw-bold"
          style={{ backgroundColor: "#000", borderColor: "#000", color: "#fff" }}
        >
          {isSuccess ? "View Transactions" : "Try Again"}
        </button>
      </div>
    </div>
  );
};

const IMAGE_BASEURL = process.env.REACT_APP_IMAGE_BASEURL || "";

const Transaction = () => {
  const [transactions, setTransactions] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'failed', or 'cancelled'

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchTransactions = async () => {
    try {
      dispatch(SET_LOADING(true));
      const res = await API.get(api_paths.member.transaction.TRANSACTION_LIST);
      if (res.data?.payload?.result) {
        setTransactions(res.data?.payload?.result || []);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  const handleDownloadPDF = async (receipt_pdf) => {
    if (!receipt_pdf) {
      toastErrorMessage("Receipt PDF is not available");
      return;
    }
    const fileURL = `${IMAGE_BASEURL}${receipt_pdf}`;
    try {
      const response = await fetch(fileURL);
      if (!response.ok) {
        toastErrorMessage("Receipt PDF is not available");
        return;
      }
      const blob = await response.blob();
      const blobURL = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobURL;
      const filename = receipt_pdf.split("/").pop() || "receipt.pdf";
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobURL);
    } catch (error) {
      console.error("PDF download error:", error);
      toastErrorMessage("Receipt PDF is not available");
    }
  };

  // Check URL params for payment status
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    const convergeId = params.get("converge_transaction_id");

    if (status) {
      setPaymentStatus({ status, convergeId });
    }
  }, [location]);

  // Handle closing the modal and cleaning the URL
  const handleCloseModal = () => {
    setPaymentStatus(null);
    navigate("/transactions", { replace: true });
    // Optional: Refresh transactions to show the new one
    // fetchTransactions();
  };

  useEffect(() => {
    fetchTransactions();
    //eslint-disable-next-line
  }, []);

  const transactionColumns = [
    { key: "name", label: "Name" },
    { key: "type", label: "Type", render: (val) => <span className="text-capitalize">{val}</span> },
    {
      key: "payment_status",
      label: "Status",
      render: (val) => <span className="text-capitalize">{val}</span>,
    },
    { key: "gateway_transaction_id", label: "Transaction ID" },
    { key: "createdAt", label: "Date", render: (val) => formatToUSDate(val?.split("T")[0]) },
    { key: "amount", label: "Amount", render: (val) => `$${val}` },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) =>
        row?.payment_status === "success" ? (
          <PiArrowCircleDownThin
            size={22}
            className="cursor-pointer text-main"
            onClick={() => handleDownloadPDF(row?.receipt_pdf)}
          />
        ) : null,
    },
  ];

  return (
    <div className="container py-4 position-relative">
      {/* Render the Modal if status exists */}
      {paymentStatus && (
        <PaymentStatusModal
          status={paymentStatus?.status}
          convergeId={paymentStatus?.convergeId}
          onClose={handleCloseModal}
        />
      )}

      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex">
            <img
              className="cursor-pointer"
              src={LeftArrow}
              alt="back-arrow"
              onClick={() => navigate("/dashboard")}
            />
            <h1 className="mb-0 fw-bold text-main f-20 ms-3">Transactions</h1>
          </div>
        </div>

        <DataTable columns={transactionColumns} data={transactions} />
      </div>
    </div>
  );
};

export default Transaction;
